import { fg } from '@atlaskit/platform-feature-flags';
import { type OriginQuery, sortMembersByType, teamsClient } from '@atlassian/teams-client';
import { type TeamMembership } from '@atlassian/teams-client/types';

import { logException, logInfoMessage } from '../../../common/utils/sentry';
import { addNewMembertoTeamSubExp } from '../../../common/utils/ufo';
import type { StoreApi, TeamAppState as TeamState } from '../types';

export const inviteMemberState = (
	teamState: TeamState,
	newMemberships: TeamMembership[],
): TeamState => {
	const { teamMembers } = teamState;

	const memberIds = new Set(newMemberships.map(({ membershipId }) => membershipId.memberId));

	const existingMembers = teamMembers.members.filter((member) => {
		return !memberIds.has(member.membershipId.memberId);
	});

	const currentUserId = teamState.myTeamMembership.membership?.membershipId.memberId;

	const finalMembers = sortMembersByType(newMemberships.concat(existingMembers), currentUserId);

	return {
		...teamState,
		teamMembers: {
			...teamState.teamMembers,
			teamId: teamState.teamMembers.teamId,
			hasLoaded: true,
			hasError: false,
			isLoading: false,
			errorType: null,
			members: finalMembers,
		},
	};
};

const fetchJobTitlesOfNewMembers =
	(teamId: string, unpopulatedMembers: TeamMembership[], originQuery: OriginQuery) =>
	async ({ getState, setState }: StoreApi) => {
		Promise.all(
			unpopulatedMembers.map((membership: TeamMembership) => {
				if (fg('migrate_clouduser_query_to_agg_user_invite_members')) {
					return teamsClient.queryAGGUser(membership.membershipId.memberId).then((user) => {
						return {
							...membership,
							user: {
								...membership.user,
								title: user.extendedProfile?.jobTitle,
							},
						} as Required<TeamMembership>;
					});
				} else {
					return teamsClient
						.queryUser(membership.membershipId.memberId, originQuery.cloudId)
						.then(({ CloudUser }) => {
							return {
								...membership,
								user: CloudUser,
							} as Required<TeamMembership>;
						});
				}
			}),
		)
			.then((memberships) => {
				const state = getState();

				// Cancel if the data we fetched is stale
				if (state.teamMembers.teamId !== teamId) {
					return;
				}

				const titles: Record<string, string | undefined> = {};

				for (const member of memberships) {
					titles[member.membershipId.memberId] = member.user.title || undefined;
				}

				setState({
					...state,
					teamMembers: {
						...state.teamMembers,
						members: state.teamMembers.members.map(
							// @ts-ignore
							(member: Required<TeamMembership>) => {
								return {
									...member,
									user: {
										...member.user,
										title: titles[member.membershipId.memberId] || member.user.title,
									},
								};
							},
						),
					},
				});
			})
			.catch((error) => {
				logException(error, 'Could not populate new team members with titles');
			});
	};

export const inviteUsersToTeam =
	(
		teamId: string,
		members: {
			id: string;
			email?: string;
			avatarUrl?: string;
			name: string;
		}[],
		originQuery: OriginQuery,
	) =>
	async ({ dispatch, getState, setState }: StoreApi) => {
		addNewMembertoTeamSubExp.addMetadata({
			apiVersion: 'v4',
		});
		const addMembershipResponse = await teamsClient.addUsersToTeam(
			teamId,
			members.map((member) => ({
				accountId: member.id,
			})),
		);

		const memberships = addMembershipResponse.members.reduce<Required<TeamMembership>[]>(
			(acc, partial) => {
				if (!partial.accountId) {
					logInfoMessage('teamInvitationInvalidResponse', {
						message: 'Membership in response was invalid',
						member: partial,
						response: addMembershipResponse,
					});

					return acc;
				}

				const user = members.find((member) => member.id === partial.accountId);

				if (!user) {
					logInfoMessage('teamInvitationInvalidResponse', {
						message: 'Could not find user details for this member',
						member: partial,
						response: addMembershipResponse,
					});

					return acc;
				}

				acc.push({
					membershipId: {
						teamId,
						memberId: partial.accountId,
					},
					// state and role are deprecated in V4, so hard code them here
					state: 'FULL_MEMBER',
					role: 'REGULAR',
					user: {
						id: user.id,
						fullName: user.name,
						avatarUrl: user.avatarUrl,
						userType: 'user',
					},
				});

				return acc;
			},
			[],
		);

		const state = getState();

		// If focused team has changed whilst invite request was resolving, abort.
		if (teamId !== state.teamMembers.teamId) {
			return;
		}

		// Update the state with the new members.
		const nextState = inviteMemberState(state, memberships);
		setState(nextState);

		// Send request to populate job titles off the main thread.
		const unpopulatedMembers = addMembershipResponse.members.map<TeamMembership>((member) => ({
			membershipId: {
				teamId,
				memberId: member.accountId,
			},
			// state and role are deprecated in V4, so hard code them here
			state: 'FULL_MEMBER',
			role: 'REGULAR',
		}));
		dispatch(fetchJobTitlesOfNewMembers(teamId, unpopulatedMembers, originQuery));

		return memberships;
	};
